import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import AlleleSearchSelect from './AlleleSearchSelect';
import { useAuth } from '../AuthContext';
import { useNotifications } from '../NotificationContext';
import { BASE_URI } from '../Config';

const CreateJob: React.FC = () => {
    const [file, setFile] = useState<File | null>(null);
    const [submissionMode, setSubmissionMode] = useState('file');
    const [modelType, setModelType] = useState('class_i');
    const [multiAlleleStrategy, setMultiAlleleStrategy] = useState('best');
    const [manualPeptides, setManualPeptides] = useState('');
    const [manualAlleles, setManualAlleles] = useState<string[]>([]);
    const inputFileRef = useRef<HTMLInputElement | null>(null);

    const { pushMessage } = useNotifications();
    const { userToken, userLineQuota } = useAuth();
    const isFileMode = submissionMode === 'file';
    const isClassIModel = modelType === 'class_i';

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (isFileMode && !file) {
            pushMessage({ text: "Please choose a CSV or TSV file to upload.", type: "error" });
            return;
        }

        if (!isFileMode) {
            const manualPeptideRows = manualPeptides.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
            if (manualPeptideRows.length === 0) {
                pushMessage({ text: "Please enter one or more peptides.", type: "error" });
                return;
            }

            if (isClassIModel && manualAlleles.length === 0) {
                pushMessage({ text: "Please choose one or more alleles.", type: "error" });
                return;
            }
        }

        const formData = new FormData();
        formData.append('submission_mode', submissionMode);
        formData.append('model_type', modelType);
        formData.append('multi_allele_strategy', multiAlleleStrategy);
        if (isFileMode && file) {
            formData.append('file', file);
        } else {
            formData.append('manual_peptides', manualPeptides);
            manualAlleles.forEach((allele) => formData.append('manual_alleles', allele));
        }

        try {
          const response = await fetch(`${BASE_URI}/mhcattention/jobs`, {
            method: 'POST',
            body: formData,
            headers: {
                'Authorization': `Bearer ${userToken}`
            }
          });

          const data = await response.json().catch(() => null);
          if (response.ok && data?.success !== false) {
            pushMessage({text: "Job created successfully!", type: "notification"});
            if (inputFileRef.current) {
                inputFileRef.current.value = '';
            }
            setFile(null);
            setManualPeptides('');
            setManualAlleles([]);
          } else if (data?.message) {
            pushMessage({text: `Problem creating job: ${data.message}`, type: "error"});
          } else if (data?.detail) {
            pushMessage({text: `Problem creating job: ${data.detail}`, type: "error"});
          } else {
            pushMessage({text: `Unexpected response, possibly due to network failure. Please try again.`, type: "error"});
          }
        } catch (error) {
          pushMessage({text: "Error sending request, please try again.", type: "error"});
        }
      };

    return (
        <form onSubmit={handleSubmit}>
            <h3>Predict MHC Class I presentation</h3>
            <div>
                <label>Mode: </label>
                <select value={submissionMode} onChange={e => setSubmissionMode(e.target.value)}>
                    <option value="manual">Manual</option>
                    <option value="file">File</option>
                </select>
            </div>
            <div>
                <label>Choose model: </label>
                <select value={modelType} onChange={e => setModelType(e.target.value)}>
                    <option value="class_i">MhcAttention</option>
                    <option value="hlae">MhcAttention HLA-E</option>
                </select>
            </div>
            {isFileMode ? (
                <div>
                <label>
                    Choose file <span className='light'>({userLineQuota ? `less than ${userLineQuota} rows` : 'within your account quota'})</span>:
                </label>
                <input type="file" accept=".csv,.tsv,text/csv,text/tab-separated-values" ref={inputFileRef} onChange={e => {
                    if (e.target.files) {
                        setFile(e.target.files[0]);
                    }
                    }}  />
                </div>
            ) : (
                <>
                    <div>
                        <label>
                            Peptides <span className='light'>({userLineQuota ? `up to ${userLineQuota} peptides` : 'within your account quota'})</span>:
                        </label>
                        <textarea
                            rows={8}
                            value={manualPeptides}
                            onChange={e => setManualPeptides(e.target.value)}
                            placeholder={"GILGFVFTL\nILAKFLHWL\nVMAPRTLIL"}
                        />
                    </div>
                    {isClassIModel && (
                        <div>
                            <label>
                                Alleles <span className='light'>(up to 6)</span>:
                            </label>
                            <div className="form_select_shell">
                                <AlleleSearchSelect value={manualAlleles} onValueChange={setManualAlleles} />
                            </div>
                        </div>
                    )}
                </>
            )}
            {isClassIModel && (
                <div>
                    <label>
                        Multi-allele strategy <span className='light'>(more <span className="tooltip_container"><button type="button" className="tooltip_link">details</button><span className="tooltip_text">Multi-allele strategy <i>Best</i> will return the best allele and its score per row, whereas the <i>All</i> strategy will expand your data to compute scores for all alleles in the allele column per row. Up to 6 alleles are allowed per row.</span></span>)</span>:
                    </label>
                    <select value={multiAlleleStrategy} onChange={e => setMultiAlleleStrategy(e.target.value)}>
                        <option value="best">Best</option>
                        <option value="all">All</option>
                    </select>
                </div>
            )}
            <div className="form_section_label instructions_label">Instructions:</div>
            <div className="submission_notes">
                {isFileMode ? (
                    <>
                        {isClassIModel ? (
                            <>
                                <p>Upload a <i>CSV</i> or <i>TSV</i> file with <i>peptide</i> and <i>alleles</i> columns. Header matching is case-insensitive.</p>
                                <p>Each <i>alleles</i> cell should contain one or more comma-separated supported allele names, with up to 6 alleles per row.</p>
                            </>
                        ) : (
                            <>
                                <p>Upload a <i>CSV</i> or <i>TSV</i> file with a <i>peptide</i> column. Header matching is case-insensitive.</p>
                                <p>For MhcAttention HLA-E, an <i>alleles</i> column is not required and will be ignored if present.</p>
                            </>
                        )}
                    </>
                ) : (
                    <>
                        {isClassIModel ? (
                            <>
                                <p>Enter one peptide per line and choose one or more supported alleles, with up to 6 alleles selected.</p>
                                <p>Manual submissions are converted into the same tabular format used for uploaded files before the job is queued.</p>
                            </>
                        ) : (
                            <>
                                <p>Enter one peptide per line. No allele selection is required for MhcAttention HLA-E.</p>
                                <p>Manual submissions are converted into the same tabular format used for uploaded files before the job is queued.</p>
                            </>
                        )}
                    </>
                )}
                <p>For more information, see the <Link to="/tutorial">Tutorial</Link>.</p>
            </div>
            <input type="submit" value="Submit"></input>
        </form>
    );
};

export default CreateJob;
